import React, { useState, useEffect } from "react"
import { Card, Col, Result, Row, Avatar, Button, Skeleton, message } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { useParams } from 'react-router-dom'
import SmartPayError from "../error"
import "./index.css"
import PartialPaymentAPIService from "../../services/api/partialPaymentService"
import { generatePDFDownloader } from "../../utility/index"

const PaymentResponse = (props) => {
    const {propsLinkData} = props
    const [txn, setTxnData] = useState({});
    const [showError, setError] = useState(false);
    const [showLoader, setLoader] = useState(true);
    const [partialPaymentConfig, setPartialPaymentConfig] = useState({});
    const { url, txn_id } = useParams();

    useEffect(() => getTxnresponse(url, txn_id), []);

    const getTxnresponse = async () => {
        if (Object.keys(propsLinkData).length > 0) {
            if (propsLinkData.status) {
                setTxnData(propsLinkData)
                setPartialPaymentConfig(propsLinkData?.data?.display_config)
            }
            setLoader(false);
        } else {
            try {
                const final_res = await PartialPaymentAPIService.get_txn_details(url, txn_id)
                if (final_res.status) {
                    setTxnData(final_res)
                    setPartialPaymentConfig(final_res?.data?.display_config)
                }
                setLoader(false);
            } catch (e) {
                setLoader(false);
                setError(true);
            }
        }
    }

    const getStatusDescription = (key) => {
        const status = {
            "success": <><p>Your payment has completed successfully</p></>,
            "userCancelled": <><p>It seems like you pressed the cancelled button or you terminated the payment proccess deliberately</p><p>Please try again to make the payment</p></>,
            "bounced": <><p>It seems like you left the card page and the payment process was terminated.</p><p>Please try again to make the payment</p></>,
            "dropped": <><p>It seems like you left the bank page and the payment process was terminated.</p><p>Please try again to make the payment</p></>,
            "pending": <><p>Your payment process is still pending. Please contact us, in case if money is debited</p></>,
            "failure": <><p>Oops! We were unable to process your payment.</p><p>Please try again</p></>,
            "default": <><p>Oops! We were unable to process your payment.</p><p>Please try again</p></>,
        }
        if (status.hasOwnProperty(key))
            return status[key]
        return status['default']
    }

    const getNestedValue = (path) => {
        if (!path) return txn;
        return path.split('.').reduce((acc, key) => acc?.[key], txn);
    };

    const DynamicFields = () => {
        let post_payment_display_fields = partialPaymentConfig?.post_payment?.display_fields || [
            {
                "key": "easepayid",
                "label": "Transaction ID",
                "value_source": "data.payment_details"
            },
            {
                "key": "txnid",
                "label": "Merchant Transaction ID",
                "value_source": "data.payment_details"
            },
            {
                "key": "amount",
                "format": "currency",
                "prefix": "₹",
                "label": "Amount",
                "value_source": "data.payment_details"
            }
        ];
        if (post_payment_display_fields.length === 0)
            return null;

        return (
            <>
                {post_payment_display_fields.map((field, index) => {
                    const sourceData = getNestedValue(field.value_source);
                    const aliasMap = field.label_from ? txn.data?.[field.label_from] || {} : {};
                    const label = field.label || aliasMap?.[field.key] || field.key;
                    const rawValue = sourceData?.[field.key];
                    let displayValue = rawValue ?? "-";

                    if (field.format === "currency" && !rawValue)
                        displayValue = parseFloat(rawValue).toFixed(2);

                    return (
                        <div key={`${field.section}-${index}`} className="spb">
                            <p><b>{label} :</b></p>
                            <p>
                                {field.prefix || ""}
                                {displayValue}
                                {field.suffix || ""}
                            </p>
                        </div>
                    );
                })}
            </>
        )
    }

    const handleDownloadReceipt = () => {
        message.loading("Please wait while we're generating your receipt", 2).then(
            new Promise((resolve, reject) => {
                try {
                    resolve(
                        generatePDFDownloader(
                            'partial-payment/response/download_receipt',
                            { url: url, txn_id: txn_id },
                            partialPaymentConfig?.post_payment?.receipt_config?.filename
                        )
                    );
                } catch (e) {
                    console.log(e)
                }
            })
        );
    }

    const AdditionalOperations = () => {
        let post_payment_config = partialPaymentConfig?.post_payment || {};
        if (!post_payment_config) return null;
        return (
            <>
                {post_payment_config?.receipt_config?.download &&
                    <Button
                        className="download-receipt"
                        style={{ color: partialPaymentConfig?.theme_color }}
                        onClick={handleDownloadReceipt}
                    >{post_payment_config?.receipt_config?.label}</Button>
                }
            </>
        )
    }

    const PrimaryButton = () => {
        const status = () => {
            if (txn.data?.payment_details?.status === "success") {
                if (txn.data.payment_made)
                    return "full_success"
                else return "partial_success"
            } else return "failure"
        }
        let primaryButtonProps = {
            "partial_success": {
                text: "Pay Remaining Amount",
                handleOnClick: e => {
                    e.preventDefault();
                    window.location.href = txn.retry;
                }
            },
            "full_success": {
                style: { display: "none" },
            },
            "failure": {
                text: "Retry Payment",
                handleOnClick: e => {
                    e.preventDefault();
                    window.location.href = txn.retry;
                }
            }
        }
        const buttonProps = primaryButtonProps[status()];
        return <Button
            type="primary"
            onClick={buttonProps.handleOnClick}
            className="pay-remaining"
            style={{
                backgroundColor: partialPaymentConfig.theme_color,
                color: partialPaymentConfig.text_color,
                ...buttonProps.style,
            }}
        >{buttonProps.text}</Button>
    }

    const ErrorDetails = () => {
        if (showError) {
            return <SmartPayError status={500} title="Transaction Not Found" errorMsg="Either we made a mistake or you are looking for something else" />
        }
        return null
    }

    const PaymentDetails = () => {
        let resultProps = {
            className: "response-result",
            title: ['success'].includes(txn.data?.payment_details?.status) ? 'Payment Successful!' : 'Payment Failed',
            status: ['success'].includes(txn.data?.payment_details?.status) ? txn.data?.payment_details?.status : 'error',
            subTitle: getStatusDescription(txn?.data?.payment_details?.status),
            icon: ['success'].includes(txn.data?.payment_details?.status) && <lottie-player
                src="https://ebz-static.s3.ap-south-1.amazonaws.com/partial_payment/Success.json"
                style={{ width: "30%", height: "30%" }} background="transparent" speed="0.80" loop autoplay />
        }
        if (txn.status) {
            return (
                <Card className="post-partial-payment-card">
                    <Skeleton loading={showLoader} active paragraph={{ rows: 20 }} >
                        <Row align="middle" className="resposne-card-header" style={{ backgroundColor: partialPaymentConfig?.theme_color, color: partialPaymentConfig?.text_color }}>
                            <Col xl={{ span: 4 }} lg={{ span: 4 }} xs={{ span: 6 }} className="avatar">
                                <Avatar src={txn.data?.m_logo} shape="square" style={{ width: "70%", height: "85%", borderRadius: "10px" }} icon={<UserOutlined />}> </Avatar>
                            </Col>
                            <Col xl={{ span: 12 }} lg={{ span: 12 }} xs={{ span: 12 }} className="d-flex merchant-data">
                                <p className="merchant-name mt-0"><b>{txn.data?.merchant_name}</b></p>
                                <p className="total-text">
                                    Transaction ID : {txn.data?.payment_details?.easepayid}
                                </p>
                            </Col>
                        </Row>
                        <Row align="middle" className="card-body-wrapper">
                            <Col className="response-card-body">
                                <Result {...resultProps} />
                                <div className="txn-details">
                                    <DynamicFields />
                                </div>
                                <div className="buttons-footer">
                                    <PrimaryButton />
                                    {['success'].includes(txn?.data?.payment_details?.status) && <AdditionalOperations />}
                                </div>
                            </Col>
                        </Row>
                    </Skeleton>
                </Card>
            )
        }
        return null
    }

    return (
        <Row align="middle" justify="center" className="post-partial-payment-row">
            <PaymentDetails />
            <ErrorDetails />
        </Row>
    )
}
export default PaymentResponse;
